/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import gql from 'graphql-tag';

import type { Theme } from '@coursera/cds-core';
import { Typography, Typography2, typography2, withTheme } from '@coursera/cds-core';

import { CML } from 'bundles/cml';
import AtGlance from 'bundles/collections/components/AtGlance';
import CollectionItem from 'bundles/collections/components/CollectionItem';
import CollectionSecondaryDescription from 'bundles/collections/components/CollectionSecondaryDescription';
import RelatedCollections from 'bundles/collections/components/RelatedCollections';
import type { CollectionPageQuery_CatalogCollectionPagesV1Resource_bySlug_elements as CatalogCollectionPage } from 'bundles/collections/components/__generated__/CollectionPageQuery';
import type { ItemType } from 'bundles/collections/types';
import { getCollectionItems } from 'bundles/collections/utils/catalogCollectionPageUtils';
import withFragments from 'bundles/graphql/components/withFragments';
import CollectionPageSchemaMarkup from 'bundles/seo/components/CollectionPageSchemaMarkup';

type PropsFromCDS = {
  theme: Theme;
};
type Props = PropsFromCDS & {
  catalogCollectionPage: CatalogCollectionPage;
  productPrimaryLanguage: string;
};

const styles = {
  gridContainer: css`
    margin-right: auto;
    margin-left: auto;
    padding-left: 15px;
    padding-right: 15px;

    &::before,
    &::after {
      display: table;
      content: ' ';
    }

    &::after {
      clear: both;
    }

    @media (min-width: 768px) {
      width: 750px;
    }

    @media (min-width: 992px) {
      width: 970px;
    }

    @media (min-width: 1200px) {
      width: 1170px;
    }

    max-width: 975px;
  `,
  collectionItemRow: css`
    display: flex;
    flex-wrap: wrap;
    margin-right: -15px;
    margin-left: -15px;
  `,
  cmlComponent: (theme: Theme) => css`
    .rc-CML {
      h2 {
        font-family: ${theme.typography.h2semibold};
      }

      p {
        font-family: ${typography2.bodyPrimary};
      }
    }
  `,
};

class CollectionItemsSection extends React.Component<Props> {
  render() {
    const {
      theme,
      catalogCollectionPage: {
        curatorHeader,
        curatorDescription,
        collection,
        s12ns,
        courses,
        degrees,
        secondaryDescription,
        relatedCollectionPages,
        title,
      },
      productPrimaryLanguage,
    } = this.props;

    const s12nsArray = s12ns?.elements ?? [];
    const coursesArray = courses?.elements ?? [];
    const degreesArray = degrees?.elements ?? [];
    const collectionItems = getCollectionItems({ collection, s12nsArray, coursesArray, degreesArray });

    const curatorHeaderCML = curatorHeader && {
      typeName: 'cml' as const,
      definition: {
        value: curatorHeader.cml.value,
        dtdId: curatorHeader.cml.dtdId,
      },
    };

    const curatorDescriptionCML = curatorDescription && {
      typeName: 'cml' as const,
      definition: {
        value: curatorDescription.cml.value,
        dtdId: curatorDescription.cml.dtdId,
      },
    };

    const getIdForCollectionItem = (item: ItemType) => {
      const typeName = item.__typename;

      if (typeName === 'OnDemandSpecializationsV1') {
        return {
          s12nId: item.id,
        };
      } else if (typeName === 'CoursesV1') {
        return {
          courseId: item.id,
        };
      } else if (typeName === 'DegreesV1') {
        return {
          degreeID: item.id,
        };
      }
      return null;
    };

    return (
      <section css={styles.gridContainer} className="p-y-3 p-x-2">
        <CollectionPageSchemaMarkup collectionItems={collectionItems} productPrimaryLanguage={productPrimaryLanguage} />
        <div css={styles.cmlComponent(theme)}>
          {curatorHeaderCML ? (
            <CML
              className="m-b-1s"
              cml={curatorHeaderCML}
              shouldRenderMath={false}
              shouldRenderCode={false}
              shouldRenderAssets={false}
            />
          ) : (
            <Typography component="h3" variant="h2">
              Curated by Coursera
            </Typography>
          )}
          {curatorDescriptionCML ? (
            <CML
              cml={curatorDescriptionCML}
              shouldRenderMath={false}
              shouldRenderCode={false}
              shouldRenderAssets={false}
            />
          ) : (
            <Typography2 variant="bodyPrimary" component="p">
              These courses and Specializations have been hand-picked by the learning team at Coursera
            </Typography2>
          )}
        </div>
        <div css={styles.collectionItemRow}>
          <div className="col-md-9 main-column m-y-2" data-e2e="collection-cards">
            {collectionItems.map(
              (item, index) =>
                item && (
                  <CollectionItem
                    data={{
                      entity_index: index, // eslint-disable-line camelcase
                      collection_title: title, // eslint-disable-line camelcase
                      slug: item.slug,
                      name: item.name,
                      ...getIdForCollectionItem(item as ItemType),
                    }}
                    item={item as ItemType}
                    key={item.id}
                  />
                )
            )}
            {secondaryDescription && <CollectionSecondaryDescription secondaryDescription={secondaryDescription} />}
            {relatedCollectionPages && <RelatedCollections relatedCollections={relatedCollectionPages} />}
          </div>
          <div className="col-md-3 side-column m-y-2">
            <AtGlance />
          </div>
        </div>
      </section>
    );
  }
}

const CollectionItemsFragment = gql`
  fragment CollectionItemsFragment on CatalogCollectionPagesV1 {
    curatorHeader {
      ... on CatalogCollectionPagesV1_cmlMember {
        cml {
          value
          dtdId
        }
      }
    }
    curatorDescription {
      ... on CatalogCollectionPagesV1_cmlMember {
        cml {
          value
          dtdId
        }
      }
    }
    secondaryDescription {
      ...SecondaryDescriptionFragment
    }
    collection {
      entries {
        id
      }
    }
    s12ns {
      elements {
        id
        name
        slug
        logo
        productVariant
        metadata {
          level
        }
        derivative {
          id
          ratingCount
          averageFiveStarRating
        }
        partners {
          elements {
            id
            name
            squareLogo
            classLogo
            logo
          }
        }
      }
    }
    courses {
      elements {
        id
        name
        slug
        level
        photoUrl
        derivative: courseDerivativesV2 {
          id
          ratingCount
          averageFiveStarRating
        }
        courseTypeMetadata {
          id
          courseTypeMetadata {
            __typename
          }
        }
        partners {
          elements {
            id
            name
            squareLogo
            classLogo
            logo
          }
        }
      }
    }
    degrees {
      elements {
        id
        name
        slug
        headerImage
        logo
        productVariant
        partners {
          elements {
            id
            name
            squareLogo
            classLogo
            logo
          }
        }
      }
    }
    relatedCollectionPages {
      ...RelatedCollectionsFragment
    }
  }
  ${RelatedCollections.fragments.RelatedCollectionsFragment}
  ${CollectionSecondaryDescription.fragments.collectionSecondaryDescriptionFragment}
`;
const collectionItemsSectionWithTheme = withTheme(CollectionItemsSection);
export default withFragments({
  collectionItemsFragment: CollectionItemsFragment,
})(collectionItemsSectionWithTheme);
